import React from 'react';
import { Document, Page, View, Text, StyleSheet } from '@react-pdf/renderer';
import TransmittalWatermark from './TransmittalWatermark';

/**
 * Submittal Transmittal - a faithful rebuild of sheet "A - Submittal Transmittal" from
 * Subtrans Example.xls.
 *
 * WHY THIS IS NOT AN EXCEL EXPORT. The deliverable is a PDF. Filling the .xls template and
 * converting would need a converter that does not exist in this codebase and is painful on
 * Azure App Service (LibreOffice / Excel interop). The app already renders PDFs with
 * react-pdf, so the form is rebuilt here against the measured geometry of the original.
 *
 * GEOMETRY. The source is 12 used columns whose Excel widths total 106.49 characters. Those
 * are scaled to the 576pt usable width of LETTER portrait at 18pt margins - the same page box
 * DsSheetPage uses - so the proportions match the original exactly rather than approximately.
 * Do not hand-tune an individual column: change the character width and let COL recompute, or
 * the row widths stop summing to 576 and the borders drift apart.
 *
 * Field names follow the workbook's own "Data" sheet, which enumerates every field on the
 * form flat and unmerged. That sheet is the contract; this component renders it.
 *
 * Duplicated rather than imported from the reports pipeline, deliberately - that pipeline is
 * shared by all 11 standard reports including the locked I/O Assignments. Same call
 * DsPackagePdfDocument made, and for the same reason.
 */

// Excel character widths, columns A..L of the source sheet.
const CHAR_W = [3.75, 7.75, 2.5, 0.12, 9.38, 7.5, 12.62, 18.88, 2.62, 17.75, 15.12, 8.5];
const TABLE_W = 576;
const SCALE = TABLE_W / CHAR_W.reduce((a, b) => a + b, 0);
const COL = CHAR_W.map(w => w * SCALE);

// Span helper: width of columns [from, to] inclusive, by zero-based index (A = 0).
const span = (from, to) => COL.slice(from, to + 1).reduce((a, b) => a + b, 0);

const THIN = 0.5;
const BORDER = '#000';

const s = StyleSheet.create({
  page:      { paddingTop: 18, paddingBottom: 18, paddingHorizontal: 18, fontFamily: 'Helvetica' },
  title:     { fontSize: 14, fontFamily: 'Helvetica-Bold', marginTop: 6 },
  project:   { fontSize: 11, fontFamily: 'Helvetica-Bold', marginTop: 2 },
  row:       { flexDirection: 'row' },
  revLetter: { width: span(0, 0), fontSize: 16, fontFamily: 'Helvetica-Bold', textAlign: 'center',
               borderTopWidth: THIN, borderBottomWidth: THIN, borderLeftWidth: THIN, borderColor: BORDER,
               paddingVertical: 2 },
  revName:   { width: span(1, 11), fontSize: 11, fontFamily: 'Helvetica-Bold', paddingLeft: 6,
               paddingVertical: 4, borderTopWidth: THIN, borderBottomWidth: THIN, borderLeftWidth: THIN,
               borderRightWidth: THIN, borderColor: BORDER },
  addrLabel: { width: span(1, 1), fontSize: 11, fontFamily: 'Helvetica-Bold' },
  addrValue: { width: span(2, 7), fontSize: 11 },
  metaLabel: { width: span(9, 9), fontSize: 10, fontFamily: 'Helvetica-Bold' },
  metaValue: { width: span(10, 11), fontSize: 11, borderBottomWidth: THIN, borderColor: BORDER,
               minHeight: 13 },
  th:        { fontSize: 9, fontFamily: 'Helvetica-Bold', borderTopWidth: THIN, borderLeftWidth: THIN,
               borderBottomWidth: THIN, borderColor: BORDER, padding: 2, textAlign: 'center' },
  thLast:    { borderRightWidth: THIN },
  td:        { fontSize: 9, borderLeftWidth: THIN, borderBottomWidth: THIN, borderColor: BORDER,
               padding: 2, minHeight: 14 },
  tdLast:    { borderRightWidth: THIN },
  chkBox:    { width: span(0, 0), fontSize: 10, fontFamily: 'Helvetica-Bold', textAlign: 'center',
               borderWidth: THIN, borderColor: BORDER, minHeight: 13 },
  chkLabel:  { fontSize: 10, fontFamily: 'Helvetica-Bold', paddingLeft: 6, paddingTop: 2 },
  sigLine:   { borderTopWidth: THIN, borderColor: BORDER, fontSize: 9, fontFamily: 'Helvetica-BoldOblique',
               paddingTop: 2 },
  // The original simulates a wet signature with a cursive font rather than an image. Helvetica-Oblique
  // is the closest the PDF core fonts get without embedding Segoe Script.
  sigText:   { fontSize: 12, fontFamily: 'Helvetica-Oblique', minHeight: 16 },
  spacer:    { height: 10 },
});

// The 11 line-item rows are fixed on the form, not an auto-growing table. More than 11 items
// is a second transmittal, which is why this truncates rather than paginating.
const ITEM_ROWS = 11;
const ITEM_COLS = [
  { w: span(0, 0), key: null,          head: 'Item No.',        align: 'center' },
  { w: span(1, 3), key: 'clientSpec',  head: 'Client Specification Section' },
  { w: span(4, 4), key: 'specPara',    head: 'Spec Paragraph / No.' },
  { w: span(5, 7), key: 'description', head: 'Title / Description' },
  { w: span(8, 9), key: 'manufacturer', head: 'Manufacturer' },
  { w: span(10, 10), key: 'submittalType', head: 'Submittal Type (product data, shop dwgs, etc.)' },
  { w: span(11, 11), key: 'leadTime',  head: 'Lead Time' },
];

const LEFT_CHECKS = [
  ['submittedForReview',     'Submitted for Review & Approval REV (0)'],
  ['resubmittedForReview',   'ReSubmitted for Review & Approval REV (1)'],
  ['substitutionInvolved',   'Substitution Involved - Substitution Request Attached'],
  ['resubmittedForRecord',   'ReSubmitted for RECORD/JOB CLOSEOUT DOC'],
  ['otherLeft',              'Other ____________________________________'],
];

const RIGHT_CHECKS = [
  ['leedSubmittal',  'LEED Submittal'],
  ['fullSubmittal',  'This is a FULL SUBMITTAL'],
  ['partialSubmittal', 'This is a PARTIAL Submittal'],
  ['otherRight',     'Other ____________________________'],
];

const Meta = ({ label, value }) => (
  <View style={[s.row, { marginBottom: 2 }]}>
    <Text style={s.metaLabel}>{label}</Text>
    <Text style={s.metaValue}>{value || ' '}</Text>
  </View>
);

const CheckRow = ({ checked, label, boxWidth, labelWidth }) => (
  <View style={[s.row, { marginBottom: 4 }]}>
    <Text style={[s.chkBox, { width: boxWidth }]}>{checked ? 'X' : ' '}</Text>
    <Text style={[s.chkLabel, { width: labelWidth }]}>{label}</Text>
  </View>
);

const TransmittalPdfDocument = ({ data = {}, watermark = null }) => {
  const items = (data.items || []).slice(0, ITEM_ROWS);

  return (
    <Document title={`Submittal Transmittal ${data.transmittalNumber || ''}`.trim()}>
      <Page size="LETTER" orientation="portrait" style={s.page}>
        <TransmittalWatermark text={watermark} />

        {/* Rows 1-6 of the source are the letterhead block. The original almost certainly
            carries a floating logo image there, which legacy .xls tooling cannot extract -
            confirm in Excel and drop it in here. Left as whitespace rather than guessed at. */}
        <View style={{ height: 54 }} />

        <Text style={s.title}>SUBMITTAL TRANSMITTAL</Text>
        <Text style={s.project}>Project Name:  {data.projectName || ''}</Text>
        <Text style={s.project}>Project Number:  {data.projectNumber || ''}</Text>

        <View style={s.spacer} />

        {/* Revision letter beside the sheet name, boxed, as on the form. */}
        <View style={s.row}>
          <Text style={s.revLetter}>{data.revision || 'A'}</Text>
          <Text style={s.revName}>Submittal Transmittal</Text>
        </View>

        <View style={s.spacer} />

        {/* To / From on the left, submission metadata on the right. */}
        <View style={s.row}>
          <View style={{ width: span(0, 8) }}>
            <View style={s.row}><Text style={s.addrLabel}>To:</Text>
              <Text style={s.addrValue}>{data.to || ''}</Text></View>
            <View style={s.row}><Text style={s.addrLabel}>Attn:</Text>
              <Text style={s.addrValue}>{data.toAttn1 || ''}</Text></View>
            <View style={s.row}><Text style={s.addrLabel} />
              <Text style={s.addrValue}>{data.toAttn2 || ''}</Text></View>
            <View style={s.row}><Text style={s.addrLabel} />
              <Text style={s.addrValue}>{data.toAttn3 || ''}</Text></View>

            <View style={{ height: 8 }} />

            <View style={s.row}><Text style={s.addrLabel}>From:</Text>
              <Text style={s.addrValue}>{data.from || ''}</Text></View>
            <View style={s.row}><Text style={s.addrLabel} />
              <Text style={s.addrValue}>{data.fromAttn1 || ''}</Text></View>
            <View style={s.row}><Text style={s.addrLabel} />
              <Text style={s.addrValue}>{data.fromAttn2 || ''}</Text></View>
            <View style={s.row}><Text style={s.addrLabel} />
              <Text style={s.addrValue}>{data.fromAttn3 || ''}</Text></View>
          </View>

          <View style={{ width: span(9, 11) }}>
            <Meta label="Date Submitted"                value={data.dateSubmitted} />
            <Meta label="Subcontractor Submittal No."   value={data.submittalNumber} />
            <Meta label="Phase/Building/Package No."    value={data.packageNumber} />
            <Meta label="Quantity Submitted (Required)" value={data.quantityRequired} />
            <Meta label="Quantity Submitted (actual)"   value={data.quantityActual} />
            <Meta label="Resubmission:"                 value={data.resubmission} />
          </View>
        </View>

        <View style={s.spacer} />

        {/* Line items - header then 11 fixed rows, blank ones still ruled. */}
        <View style={s.row}>
          {ITEM_COLS.map((c, i) => (
            <Text key={c.head}
              style={[s.th, { width: c.w }, i === ITEM_COLS.length - 1 ? s.thLast : null]}>
              {c.head}
            </Text>
          ))}
        </View>

        {Array.from({ length: ITEM_ROWS }, (_, r) => {
          const item = items[r] || {};
          return (
            <View style={s.row} key={r}>
              {ITEM_COLS.map((c, i) => (
                <Text key={c.head}
                  style={[s.td, { width: c.w },
                          c.align === 'center' ? { textAlign: 'center', fontFamily: 'Helvetica-Bold' } : null,
                          i === ITEM_COLS.length - 1 ? s.tdLast : null]}>
                  {c.key ? (item[c.key] || ' ') : `${r + 1}.0`}
                </Text>
              ))}
            </View>
          );
        })}

        <View style={s.spacer} />

        {/* Checkbox block. The original is a literal "X" in a bordered cell, not a form
            control, so that is what this draws. */}
        <View style={s.row}>
          <View style={{ width: span(0, 8) }}>
            {LEFT_CHECKS.map(([key, label]) => (
              <CheckRow key={key} checked={!!data[key]} label={label}
                boxWidth={span(0, 0)} labelWidth={span(1, 8)} />
            ))}
          </View>
          <View style={{ width: span(9, 11) }}>
            {RIGHT_CHECKS.map(([key, label]) => (
              <CheckRow key={key} checked={!!data[key]} label={label}
                boxWidth={span(8, 8)} labelWidth={span(9, 11) - span(8, 8)} />
            ))}
          </View>
        </View>

        <View style={s.spacer} />

        {/* Signature block: two panels, each a signature line over its caption. */}
        <View style={[s.row, { marginTop: 12 }]}>
          <View style={{ width: span(0, 8) }}>
            <Text style={s.sigText}>{data.receivingSignature || ' '}</Text>
            <Text style={[s.sigLine, { width: span(4, 7) }]}>Signature of Receiving Party</Text>
          </View>
          <View style={{ width: span(9, 11) }}>
            <Text style={s.sigText}>{data.receivingDate || ' '}</Text>
            <Text style={[s.sigLine, { width: span(9, 11) }]}>Signed Date</Text>
          </View>
        </View>

        <View style={[s.row, { marginTop: 14 }]}>
          <View style={{ width: span(0, 8) }}>
            <Text style={s.sigText}>{data.transferringSignature || ' '}</Text>
            <Text style={[s.sigLine, { width: span(4, 7) }]}>Signature of Transferring Party</Text>
          </View>
          <View style={{ width: span(9, 11) }}>
            <Text style={s.sigText}>{data.transferringDate || ' '}</Text>
            <Text style={[s.sigLine, { width: span(9, 11) }]}>Signed Date</Text>
          </View>
        </View>

      </Page>
    </Document>
  );
};

export default TransmittalPdfDocument;
